import React, { useState, useContext, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { formatDate, parseDate } from '@perch-labs/warbler';
import {
  Alert,
  Button,
  Text as ShrikeText,
  Notification,
} from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { BackLink } from 'components/back-link';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { statementStatuses } from 'domain/statement';
import { useAsync } from 'hooks/use-async.hook';
import { useStatement } from 'hooks/use-statement.hook';
import { translateError } from 'utils/errors/translate-error';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { Text, Page } from 'styles';
import {
  DELAY_PAYMENT_MUTATION,
  DELAY_PAYMENT_REFETCH_QUERY,
} from './delay-payment.api';
import { StyledDatePicker } from './delay-payment.style';

const getCopy = copyFor('billing.dashboard.latestStatement.delayPayment');
const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

interface Props {
  location?: {
    state: {
      from: string;
      fromLabel: string;
    };
  };
}

export const DelayPayment = (props: Props) => {
  const history = useHistory();
  const { planId, statementId = '' } =
    useParams<{ planId?: string; statementId?: string }>();
  const { billing, statement: statementRoute } = routes({
    planId,
    statementId,
  });
  const { from, fromLabel } = props.location?.state || {};
  const statement = useStatement({ id: statementId });
  const [invalidStatement, setInvalidStatement] = useState(false);
  const [newPaymentScheduledAt, setNewPaymentScheduledAt] = useState<
    Date | undefined
  >();
  const { addNotification } = useContext(Notification.Context);
  const [mutate] = useMutation(DELAY_PAYMENT_MUTATION);

  const navigateToStatement = () => history.push(statementRoute);

  useEffect(() => {
    if (invalidStatement) {
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('ineligibleNotification'),
        onAdd: navigateToStatement,
        variant: 'warning',
      });
    }
  }, [invalidStatement]);

  const [{ error: mutationError, loading: mutationLoading }, submit] = useAsync(
    async () => {
      await mutate({
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: DELAY_PAYMENT_REFETCH_QUERY,
            variables: { id: parseInt(statementId) },
          },
        ],
        variables: {
          input: {
            paymentScheduledAt: newPaymentScheduledAt,
            source: 'SEAGULL',
            statementId: parseInt(statementId),
          },
        },
      });
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('notification'),
        onAdd: navigateToStatement,
        variant: 'success',
      });
    }
  );

  const handleSubmit = e => {
    e.preventDefault();
    submit();
  };

  if (statement.loading) return <LoadingPage />;
  if (statement.error) return <ErrorPage />;

  if (!statementId || statement.data?.status !== statementStatuses.issued) {
    if (!invalidStatement) setInvalidStatement(true);
    return null;
  }

  return (
    <>
      <BackLink
        location={fromLabel || getBackLinkCopy('billing')}
        to={from || billing}
      />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <ShrikeText tag="h2" textStyle="heading800">
          {getCopy('title')}
        </ShrikeText>
        <Text margin={{ bottom: '24px', top: '16px' }}>
          {getCopy('subtitle')}
        </Text>
        <form onSubmit={handleSubmit}>
          <StyledDatePicker
            label="Available dates"
            handleChange={date => setNewPaymentScheduledAt(date || undefined)}
            reactDatePickerProps={{
              inline: true,
              maxDate: parseDate(statement.data.maxDelayDate),
              minDate: parseDate(statement.data.paymentScheduledAt),
            }}
            value={newPaymentScheduledAt}
          />
          <Text
            textAlign="center"
            textStyle="heading500"
            margin={{ top: '48px' }}
          >
            {formatDate(newPaymentScheduledAt || '', {
              format: 'EEEE, MMMM dd, yyyy',
            })}
          </Text>
          <Text textAlign="center" margin={{ bottom: '48px', top: '16px' }}>
            {getCopy('description')}
          </Text>
          <Alert margin={{ bottom: '16px' }}>
            {translateError(mutationError)}
          </Alert>
          <Button
            fullWidth
            loading={mutationLoading}
            disabled={!newPaymentScheduledAt}
            margin={{ bottom: '12px' }}
            type="submit"
          >
            {getCopy('buttons.submit')}
          </Button>
        </form>
      </Page>
    </>
  );
};
